import { useTranslation } from 'react-i18next';
import { useInfluencerPackagePermissions, useUserRoles } from '@anytag/auth';
import { ProposalJobDescriptionBreakDownMode, UserProposalJobSummary } from '@anytag/graphql';
import { AddPackageWithAccounts } from '@anytag/shared-organisms';
import { formatIntNumber, formatPercent, formatPrice, getSocialPairsIds } from '@anytag/shared-utils';
import { useUserProposalContext } from './UserProposalContext';
import { ProposeInfluencersDownloadButton } from './ProposeInfluencersDownloadButton';

interface ProposalSummaryProps {
  summary?: UserProposalJobSummary;
  currency?: string;
}
export const ProposalSummary = ({ summary, currency }: ProposalSummaryProps) => {
  const { t } = useTranslation();
  const { isAdminStaffTalentAgencyPartner } = useUserRoles();
  const { hideAddPackageBtn } = useInfluencerPackagePermissions();
  const { allSocialAccounts, userProposal, selectedInfluencers } = useUserProposalContext();
  const { socialPairs } = getSocialPairsIds(allSocialAccounts);

  const stats = [
    {
      label: t('General.Total Followers'),
      value: formatIntNumber(summary?.followersCount ? Number(summary?.followersCount) : null)
    },
    {
      unit: '%',
      label: t('HeaderColumn.Agg Egg Rate by views'),
      value: formatPercent(summary?.engagementViewRate, false)
    },
    {
      unit: '%',
      label: t('HeaderColumn.Agg Egg Rate by followers'),
      value: formatPercent(summary?.engagementFollowerRate, false)
    },
    {
      label: t('JobDescription.Budget'),
      value: formatPrice(summary?.budget, currency)
    },
    ...(isAdminStaffTalentAgencyPartner
      ? [
          {
            label: t('JobDescription.Influencer Cost'),
            value: formatPrice(summary?.influencerCost, currency),
            unit: currency ? t(currency) : ''
          },
          {
            label: t('JobDescription.Profit'),
            value: formatPrice(summary?.profit, currency),
            unit: currency ? t(currency) : ''
          },
          {
            label: t('HeaderColumn.Avg Engagement Proposed'),
            value: formatIntNumber(summary?.engagementProposedCount)
          },
          {
            label: t('HeaderColumn.Avg Engagement Posted'),
            value: formatIntNumber(summary?.engagementPostedCount)
          }
        ]
      : [])
  ];

  return (
    <div className="bg-white">
      <div className="box-border flex h-12 items-center justify-between border-b border-border px-4 py-2">
        <p className="text-lg leading-4 font-semibold text-primary">{t('Proposal List')}</p>
        <div className="flex items-center">
          {!hideAddPackageBtn ? (
            <AddPackageWithAccounts
              influencerIds={selectedInfluencers.map((infl) => infl.id)}
              socialPairs={socialPairs}
              isInfluencerAccount={userProposal?.jdMode === ProposalJobDescriptionBreakDownMode.INFLUENCER_BREAKDOWN}
            />
          ) : null}
          <ProposeInfluencersDownloadButton />
        </div>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] items-center gap-2 p-2">
        {stats.map(({ label, value, unit }, index) => (
          <div key={index} className="w-36 border-l-border p-2">
            <div className="overflow-hidden text-sm font-semibold text-ellipsis text-tertiary">{label}</div>
            <div
              title={`${value} ${unit || ''}`}
              className="mt-1 overflow-hidden text-lg font-semibold text-ellipsis whitespace-nowrap text-primary [&>span]:text-sm [&>span]:text-tertiary"
            >
              {value}
              &nbsp;
              {unit ? <span>{unit}</span> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
